// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: other agent chats and pull requests mentioned in an Agents prompt. In the composer, drafts
// and the outbox a mention is a Markdown link: `[title](<web app>/agents/<machine>/<thread>)` for a
// chat, `[#302 title](https://github.com/<owner>/<repo>/pull/302)` for a pull request, shown as a
// chip. On send the links become `[label](mention:<ref>)` with what they point at in `mentions`
// (the API summarises chats on other machines); read back, the bridge's `[label](mention:<n>)`
// becomes the link again.

import type {AgentMentionRef, AgentPullRequest, AgentThreadSummary} from '@fluxer/schema/src/domains/konvo/Agents';

/** A chat by machine and thread, or a pull request, as the API takes them. */
export type AgentMentionInput =
	| {ref: string; kind: 'chat'; machineId: string; threadId: string; title?: string}
	| {
			ref: string;
			kind: 'pull';
			repo: string;
			number: number;
			title: string;
			url: string;
			state?: 'open' | 'closed' | 'merged';
			draft?: boolean;
	  };

const LINK = /\[([^\]\n]{1,200})\]\(([^)\s]{1,500})\)/g;
const WIRE_LINK = /\[([^\]\n]{0,200})\]\(mention:([a-z0-9_-]{1,40})\)/gi;
const PULL_URL = /^https:\/\/github\.com\/([\w.-]+\/[\w.-]+)\/pull\/(\d{1,9})\/?$/;
const AGENT_PATH = /^\/agents\/([^/?#]{1,40})\/([^/?#]{1,200})\/?$/;
/** Mentions one prompt may carry (AGENT_MENTIONS_MAX). */
export const MENTIONS_MAX = 10;

/** A chat's link, on the web app's origin so it opens Konvo anywhere it's pasted. */
export function chatMentionUrl(origin: string, machineId: string, threadId: string): string {
	return `${origin.replace(/\/+$/, '')}/agents/${encodeURIComponent(machineId)}/${encodeURIComponent(threadId)}`;
}

/** The chat a Konvo link points at: an agents path on one of `origins` (the web app's, this page's). */
export function parseChatMentionUrl(
	url: string,
	origins: ReadonlyArray<string>,
): {machineId: string; threadId: string} | null {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return null;
	}
	if (!origins.some((origin) => sameOrigin(origin, parsed.origin))) return null;
	const match = AGENT_PATH.exec(parsed.pathname);
	if (!match) return null;
	try {
		return {machineId: match[1]!, threadId: decodeURIComponent(match[2]!)};
	} catch {
		return null;
	}
}

function sameOrigin(a: string, b: string): boolean {
	try {
		return new URL(a).origin === new URL(b).origin;
	} catch {
		return false;
	}
}

export function parsePullUrl(url: string): {repo: string; number: number} | null {
	const match = PULL_URL.exec(url);
	return match ? {repo: match[1]!, number: Number(match[2])} : null;
}

export function isMentionUrl(url: string, origins: ReadonlyArray<string>): boolean {
	return parsePullUrl(url) !== null || parseChatMentionUrl(url, origins) !== null;
}

/** A pull request's chip label: its number, then its title. */
export function pullLabel(pull: {number: number; title?: string}): string {
	return pull.title ? `#${pull.number} ${pull.title}` : `#${pull.number}`;
}

/**
 * The prompt as sent: each mention link becomes `[label](mention:<ref>)`, and what it points at
 * goes in `mentions` (at most MENTIONS_MAX; links past that stay links).
 */
export function mentionWire(
	prompt: string,
	origins: ReadonlyArray<string>,
	pullInfo: (url: string) => Partial<AgentPullRequest> | null = () => null,
): {prompt: string; mentions: Array<AgentMentionInput>} {
	const mentions: Array<AgentMentionInput> = [];
	const refs = new Map<string, string>();
	const text = prompt.replace(LINK, (whole, label: string, url: string) => {
		let ref = refs.get(url);
		if (!ref) {
			if (mentions.length >= MENTIONS_MAX) return whole;
			const chat = parseChatMentionUrl(url, origins);
			const pull = chat ? null : parsePullUrl(url);
			if (!chat && !pull) return whole;
			ref = `m${mentions.length + 1}`;
			refs.set(url, ref);
			if (chat) mentions.push({ref, kind: 'chat', ...chat, title: label.slice(0, 200)});
			else {
				const known = pullInfo(url);
				const title = known?.title ?? label.replace(/^#\d+\s*/, '');
				mentions.push({
					ref,
					kind: 'pull',
					repo: pull!.repo,
					number: pull!.number,
					title: (title || `#${pull!.number}`).slice(0, 300),
					url,
					...(known?.state ? {state: known.state} : {}),
					...(known?.draft ? {draft: true} : {}),
				});
			}
		}
		return `[${label}](mention:${ref})`;
	});
	return {prompt: text, mentions};
}

/**
 * A prompt read back from the bridge as Markdown with mention links: `[label](mention:<n>)` points
 * at `mentions[n]`. A chat without a machine is on `machineId`, the chat's own.
 */
export function mentionDisplay(
	text: string,
	mentions: ReadonlyArray<AgentMentionRef> | undefined,
	machineId: string,
	origin: string,
): string {
	if (!mentions?.length || !text.includes('](mention:')) return text;
	return text.replace(WIRE_LINK, (_whole, label: string, ref: string) => {
		const mention = mentions[Number(ref)];
		if (!mention) return label;
		const url =
			mention.kind === 'pull' ? mention.url : chatMentionUrl(origin, mention.machineId ?? machineId, mention.threadId);
		return `[${label || mention.title}](${url})`;
	});
}

/** Any Markdown link or picture: a mention, an attached file, a web link. */
const ANY_LINK = /!?\[([^\]\n]{0,512})\]\(([^)\s]{1,800})\)/g;

/**
 * A prompt's words without where its links point, to tell the copy the thread brings back from the
 * one sent: mentions go out as links to GitHub or a chat and come back as `mention:<n>`, attached
 * files by another name, and T3 tidies a label's brackets and spaces (as the bridge's `label`).
 */
export function promptWords(prompt: string): string {
	return prompt
		.replace(
			ANY_LINK,
			(_whole, label: string) =>
				` ${label
					.replace(/[[\]\\\r\n]/g, ' ')
					.replace(/\s+/g, ' ')
					.trim()
					.slice(0, 200)} `,
		)
		.replace(/\s+/g, ' ')
		.trim();
}

/** The `@` word being typed right before the caret: its start and what follows the `@`. */
export function mentionQuery(value: string, caret: number): {start: number; query: string} | null {
	const before = value.slice(0, caret);
	const match = /(^|\s)@([^\s@[\]()]{0,40})$/.exec(before);
	if (!match) return null;
	return {start: caret - match[2]!.length - 1, query: match[2]!};
}

export type MentionChoice =
	| {
			kind: 'chat';
			key: string;
			machineId: string;
			machineName: string;
			thread: AgentThreadSummary;
			projectTitle?: string;
	  }
	| {kind: 'pull'; key: string; pull: MentionPull};

/**
 * A pull request the picker offers: one a chat opened (`fromChat`), or one open on GitHub in the
 * chat's repository (GET /konvo/agents/pulls/open), with who wrote it, whether it asks for your
 * review and how its checks stand.
 */
export type MentionPull = AgentPullRequest & {
	fromChat?: boolean;
	author?: string;
	review?: boolean;
	checks?: {state: 'passed' | 'failed' | 'running'; failed: number};
};

const isOpen = (pull: MentionPull) => pull.state !== 'closed' && pull.state !== 'merged';

/** The same pull request listed twice (a chat's and GitHub's) becomes one, with what each knows. */
export function mergePulls(pulls: ReadonlyArray<MentionPull>): Array<MentionPull> {
	const byUrl = new Map<string, MentionPull>();
	for (const pull of pulls) {
		const key = pull.url.toLowerCase();
		const seen = byUrl.get(key);
		if (!seen) {
			byUrl.set(key, pull);
			continue;
		}
		// Later fields fill in or update earlier ones; one from a chat stays from a chat.
		const known = Object.fromEntries(Object.entries(pull).filter(([, value]) => value !== undefined));
		byUrl.set(key, {...seen, ...known, fromChat: !!(seen.fromChat || pull.fromChat)});
	}
	return [...byUrl.values()];
}

/**
 * What a query finds: chats by title, pull requests by number or title, best matches first. A query
 * that starts with `#` (typed `@#`) is pull requests only, and more of them; ones asking for your
 * review come first, then the chat's own repository's, open ones before closed and merged, then the chats' own.
 */
export function matchMentions(
	query: string,
	chats: ReadonlyArray<Extract<MentionChoice, {kind: 'chat'}>>,
	pulls: ReadonlyArray<MentionPull>,
	{exclude, here, repos = []}: {exclude?: string; here?: string; repos?: ReadonlyArray<string>} = {},
): {chats: Array<Extract<MentionChoice, {kind: 'chat'}>>; pulls: Array<Extract<MentionChoice, {kind: 'pull'}>>} {
	const onlyPulls = query.trim().startsWith('#');
	const q = query.trim().toLowerCase().replace(/^#/, '');
	// A bare `@` shows a few of each, so pull requests show without scrolling; typing narrows either.
	const bare = !q && !onlyPulls && pulls.length > 0;
	const ours = new Set(repos.map((repo) => repo.toLowerCase()));
	const inRepo = (pull: MentionPull) => ours.has(pull.repository.toLowerCase());
	const score = (text: string) => {
		const t = text.toLowerCase();
		if (!q) return 1;
		if (t.startsWith(q)) return 3;
		if (t.split(/[\s/._-]+/).some((word) => word.startsWith(q))) return 2;
		return t.includes(q) ? 1 : 0;
	};
	const recent = (thread: AgentThreadSummary) => Date.parse(thread.updatedAt) || 0;
	const foundChats = (onlyPulls ? [] : chats)
		.filter((chat) => chat.key !== exclude)
		.map((chat) => ({chat, s: score(chat.thread.title)}))
		.filter(({s}) => s > 0)
		.sort(
			(a, b) =>
				b.s - a.s ||
				Number(b.chat.machineId === here) - Number(a.chat.machineId === here) ||
				recent(b.chat.thread) - recent(a.chat.thread),
		)
		.slice(0, bare ? 2 : 6)
		.map(({chat}) => chat);
	const foundPulls = mergePulls(pulls)
		.map((pull) => ({pull, s: Math.max(score(String(pull.number)), score(pull.title ?? ''))}))
		.filter(({s}) => s > 0)
		.sort(
			(a, b) =>
				b.s - a.s ||
				Number(!!b.pull.review) - Number(!!a.pull.review) ||
				Number(inRepo(b.pull)) - Number(inRepo(a.pull)) ||
				Number(isOpen(b.pull)) - Number(isOpen(a.pull)) ||
				Number(!!b.pull.fromChat) - Number(!!a.pull.fromChat) ||
				b.pull.number - a.pull.number,
		)
		.slice(0, onlyPulls ? 8 : bare ? 4 : 5)
		.map(({pull}) => ({kind: 'pull' as const, key: pull.url, pull}));
	return {chats: foundChats, pulls: foundPulls};
}
